import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import { MEMES } from "../../lib/memeData";

const TROLL_LINES = [
  "💥 BOOM. you killed the meme. refresh to undo, retard.",
  "you broke it. it's gone forever. (or until you hit F5)",
  "RIP. didn't think it through huh?",
  "congrats, you blew it up. literally. refresh = revive.",
  "💀 meme deleted. you have been added to the naughty list.",
  "imagine clicking a meme to blow it up. you did that. refresh.",
  "the meme has ascended. press F5 to summon it back.",
];

const EXPLODE_MS = 500;   // visual blow-up duration

const Polaroid = ({ meme, idx, phase, onBoom }) => {
  // phase: "alive" | "exploding" | "collapsing"
  if (phase === "collapsing") {
    return (
      <span
        className="shrink-0 inline-block transition-[width,margin] duration-300 ease-in"
        style={{ width: 0, margin: 0 }}
        aria-hidden="true"
      />
    );
  }

  const isExploding = phase === "exploding";

  return (
    <button
      type="button"
      onClick={() => phase === "alive" && onBoom(idx)}
      disabled={phase !== "alive"}
      className={`shrink-0 w-56 md:w-64 bg-white border-4 border-[#1A1A1A] shadow-[6px_6px_0_#1A1A1A] p-3 mx-3 cursor-pointer transition-all duration-500 ease-out ${
        isExploding
          ? "opacity-0 scale-[2.4] rotate-[35deg] pointer-events-none blur-sm"
          : "hover:-translate-y-1 hover:shadow-[8px_10px_0_#1A1A1A]"
      }`}
      style={{
        transform: isExploding ? undefined : `rotate(${meme.rotation || 0}deg)`,
      }}
      data-testid={`meme-card-${idx}`}
      aria-label={`boom: ${meme.caption}`}
    >
      <div className="relative">
        <span className="tape" style={{ width: 70, height: 18, top: -10 }} />
        <img
          src={meme.src}
          alt={meme.caption}
          loading="lazy"
          draggable={false}
          className="w-full aspect-square object-cover border-2 border-[#1A1A1A] bg-[#FBF3E2] pointer-events-none"
        />
      </div>
      <figcaption className="mt-3 font-display text-lg md:text-xl text-[#1A1A1A] text-center leading-tight">
        {meme.caption}
      </figcaption>
    </button>
  );
};

export const MemeGallery = () => {
  // Index in MEMES -> phase. Missing = alive.
  const [phases, setPhases] = useState(() => new Map());

  const handleBoom = useCallback((cardIdx) => {
    const memeIdx = cardIdx % MEMES.length;
    setPhases((prev) => {
      if (prev.has(memeIdx)) return prev;
      const next = new Map(prev);
      next.set(memeIdx, "exploding");
      return next;
    });

    // After explosion finishes, collapse the slot so the flywheel closes the gap.
    setTimeout(() => {
      setPhases((prev) => {
        const next = new Map(prev);
        next.set(memeIdx, "collapsing");
        return next;
      });
    }, EXPLODE_MS);

    const line = TROLL_LINES[Math.floor(Math.random() * TROLL_LINES.length)];
    toast(line, { duration: 4000 });
  }, []);

  // After collapse, items remain rendered as 0-width placeholders (cheap).
  const loop = [...MEMES, ...MEMES];
  const aliveCount = MEMES.filter((_, i) => !phases.has(i) || phases.get(i) === "exploding").length;
  const allGone = aliveCount === 0;

  return (
    <section id="memes" className="py-14 overflow-hidden" data-testid="meme-gallery-section">
      <div className="px-5 md:px-10 max-w-6xl mx-auto text-center mb-8">
        <h2 className="font-display text-5xl md:text-7xl">
          <span className="scribble-underline">the retard gallery</span>
        </h2>
        <p className="text-lg md:text-xl text-[#1A1A1A]/70 mt-2">
          hover to pause. tap any meme to blow it up 💥 (don't say we didn't warn you)
        </p>
      </div>

      {allGone ? (
        <div className="px-5 md:px-10 max-w-3xl mx-auto crayon-card text-center py-10 -rotate-1" data-testid="all-memes-gone">
          <div className="text-7xl mb-3">💀</div>
          <h3 className="font-display text-4xl md:text-5xl">you killed every meme</h3>
          <p className="text-lg md:text-xl text-[#1A1A1A]/70 mt-2">
            congratulations retard. only one way back. refresh the page.
          </p>
        </div>
      ) : (
        <div className="relative">
          <div className="flex marquee-track w-max items-stretch" data-testid="meme-marquee">
            {loop.map((m, idx) => {
              const memeIdx = idx % MEMES.length;
              const phase = phases.get(memeIdx) || "alive";
              return (
                <Polaroid
                  key={idx}
                  meme={m}
                  idx={idx}
                  phase={phase}
                  onBoom={handleBoom}
                />
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default MemeGallery;
